import { useState, useEffect } from "react";

function getDeviceType() {
  const width = window.innerWidth;

  const isPhoneAgent =
    /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
      navigator.userAgent
    );

  // Mobile phones
  if (width <= 768 || (isPhoneAgent && width <= 900)) {
    return "mobile";
  }

  return "desktop";
}

export function useDeviceType() {
  const [deviceType, setDeviceType] = useState(() => getDeviceType());

  useEffect(() => {
    const check = () => setDeviceType(getDeviceType());

    check();

    window.addEventListener("resize", check);

    return () => {
      window.removeEventListener("resize", check);
    };
  }, []);

  return deviceType;
}

// Treat tablet landscape sizes (e.g. 1280x720) as big-screen mode.
function checkIsMobile() {
  const width = window.innerWidth;
  const byWidth = width <= 900;

  // Phone-sized UA fallback for cases where viewport reporting is unstable.
  const isPhoneAgent = /Android.*Mobile|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i
    .test(navigator.userAgent);

  return byWidth || (isPhoneAgent && width <= 1280);
}

export function useMobile() {
  const [isMobile, setIsMobile] = useState(() => checkIsMobile());

  useEffect(() => {
    const check = () => setIsMobile(checkIsMobile());
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return isMobile;
}

export function usePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled,   setIsInstalled]   = useState(false);
  const [dismissed,     setDismissed]     = useState(() => {
    try { return localStorage.getItem("pwa-banner-dismissed") === "true"; }
    catch { return false; }
  });

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setIsInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setInstallPrompt(null);
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("pwa-banner-dismissed", "true"); } catch {}
  };

  return { install, dismiss, showBanner: !isInstalled && !dismissed && !!installPrompt, isInstalled };
}
